<template>
    <div class="columns">
        <div class="column">
            <SimpleCard
                data-test="card"
                title="Most recent revenue"
                :loading="yieldLoading"
                :title_2="
                    yieldMetadata && yieldMetadata['last_valid_row']
                        ? formatDate(makeDateObject(yieldMetadata['last_valid_row']['date']))
                        : ''
                "
                :value="
                    yieldMetadata &&
                    yieldMetadata['last_valid_row'] &&
                    yieldMetadata['last_valid_row']['publisher revenue'] > 0
                        ? numberFormatWithSpaceRound(
                              yieldMetadata['last_valid_row']['publisher revenue'] / exchangeRate
                          ) +
                          ' ' +
                          currencySymbol
                        : 'N/A'
                "
                color="success"
            />
        </div>

        <div class="column">
            <SimpleCard
                data-test="card"
                title="Revenue for the selected period"
                :loading="yieldLoading"
                :value="numberFormatWithSpaceRound(revenueSelectedPeriod)"
                :postfix="' ' + currencySymbol"
                color="success"
            />
        </div>

        <div class="column">
            <SimpleCard
                data-test="card"
                title="Revenue Grand Total"
                :loading="yieldLoading"
                :value="revenueGrandTotal"
                :postfix="' ' + currencySymbol"
                :font_size="3.6"
                color="grand_total"
            />
        </div>
    </div>

    <div class="columns">
        <div class="column">
            <SimpleCard
                data-test="card"
                title="Impressions for the selected period"
                :loading="yieldLoading"
                :value="numberFormat(impressionsSelectedPeriod, 3)"
            />
        </div>

        <div class="column">
            <SimpleCard
                data-test="card"
                title="RPM for the selected period"
                :loading="yieldLoading"
                :value="rpmSelectedPeriod"
                :postfix="' ' + currencySymbol"
                :is-decimal="true"
            />
        </div>
    </div>

    <div class="columns">
        <div class="column">
            <SimpleCard
                data-test="card"
                title="Most recent impressions"
                :loading="yieldLoading"
                :title_2="
                    yieldMetadata && yieldMetadata['last_valid_row']
                        ? formatDate(makeDateObject(yieldMetadata['last_valid_row']['date']))
                        : ''
                "
                :value="
                    yieldMetadata &&
                    yieldMetadata['last_valid_row'] &&
                    yieldMetadata['last_valid_row']['impressions'] > 0
                        ? numberFormat(yieldMetadata['last_valid_row']['impressions'], 3)
                        : 'N/A'
                "
            />
        </div>

        <div class="column">
            <SimpleCard
                :without-format-cell="true"
                data-test="card"
                title="Most recent RPM"
                :loading="yieldLoading"
                :title_2="
                    yieldMetadata && yieldMetadata['last_valid_row']
                        ? formatDate(makeDateObject(yieldMetadata['last_valid_row']['date']))
                        : ''
                "
                :value="
                    yieldMetadata && yieldMetadata['last_valid_row'] && yieldMetadata['last_valid_row']['rpm'] > 0
                        ? numberFormatWithSpaceRound(yieldMetadata['last_valid_row']['rpm'] / exchangeRate) +
                          ' ' +
                          currencySymbol
                        : 'N/A'
                "
                :is-decimal="rpmYesterday > 0"
            />
        </div>

        <div class="column">
            <SimpleCard
                data-test="card"
                title="Impressions Grand Total"
                :loading="yieldLoading"
                :value="numberFormat(impressionsGrandTotal, 3)"
            />
        </div>

        <div class="column">
            <SimpleCard
                data-test="card"
                title="RPM Grand Total"
                :loading="yieldLoading"
                :value="rpmGrandTotal"
                :postfix="' ' + currencySymbol"
                :is-decimal="true"
            />
        </div>
    </div>

    <div class="columns" v-if="hasValidYieldData">
        <div class="column">
            <LaYieldDataCard
                :yield-data="yieldData"
                :yield-metadata="yieldMetadata"
                :yield-csv-data="yieldCsvData"
                :yield-loading="yieldLoading"
                :domains="domains"
            />
        </div>
    </div>
    <div class="columns" v-else-if="yieldLoading">
        <div class="column">
            <LaDataCardLoader
                card
                :chart-count="domains?.length > 1 || yieldMetadata?.['use_slot_data'] ? 2 : 1"
                :chart-height="domains?.length > 1 ? 500 : 350"
            />
        </div>
    </div>

    <LaNoData
        v-if="!yieldMetadata && !yieldLoading"
        description="Check if you added the measurement script correctly to your page or contact our support."
    />
</template>

<script>
import LaNoData from "@/components/NoData";
import LaDataCardLoader from "@/components/loaders/DataCardLoader";
import LaYieldDataCard from "@/components/yield/YieldDataCard";
import {
    getExchangeRateFromCzk,
    getCurrencySymbol,
    numberFormat,
    numberFormatWithSpaceRound,
    exchange_rate,
} from "@/helpers/data";
import { formatDate } from "@/helpers/filters.js";
import store from "@/store/store";
import { PeriodsEnum } from "@/helpers/data";
import { ref, computed, watch } from "vue";
import {
    getterTypes as yieldGetterTypes,
    actionTypes as yieldActionTypes,
    mutationTypes as yieldMutationTypes,
} from "@/store/Yield/types";
import { getterTypes as publisherGetterTypes } from "@/store/Publisher/types";
import { getterTypes } from "@/store/LoggedUser/types";
import SimpleCard from "@/components/SimpleCard";

let device = "desktop";

export default {
    name: "LaYieldCards",
    methods: {
        exchange_rate() {
            return exchange_rate;
        },
        numberFormat,
        numberFormatWithSpaceRound,
        makeDateObject(yyyy_mm_dd) {
            return new Date(yyyy_mm_dd);
        },
        formatDate,
        getExchangeRateFromCzk,
        getCurrencySymbol,
    },
    computed: {
        PeriodsEnum() {
            return PeriodsEnum;
        },
    },
    components: {
        SimpleCard,
        LaDataCardLoader,
        LaNoData,
        LaYieldDataCard,
    },
    props: {
        payload: {
            type: Object,
            required: true,
        },
        websites: {
            required: true,
            type: Array,
        },
    },
    emits: ["update-available-slot-names"],
    setup(props, { emit }) {
        // Drop metadata left in the Yield store by a previous mount before any
        // watcher can read it. Leaving the dashboard only clears WebsiteDates, so
        // metadata.available_slot_names survives; the slotNames watcher below would
        // emit that stale list, the parent would put it into the payload, and the
        // resulting second fetch — slot-filtered, often empty — would overwrite the
        // correct data from the first one. In-place refetches (period/website change)
        // keep their metadata, this only runs on mount.
        store.commit(yieldMutationTypes.metadataReset);

        const userRights = computed(() => store.getters[getterTypes.getUserRights]);
        const isSuperAdmin = computed(() => userRights.value && userRights.value["super_admin"]);

        const selectCurrency = computed(() => store.getters[publisherGetterTypes.getSelectedPublisherCurrency]);
        const exchangeRate = computed(() => getExchangeRateFromCzk(selectCurrency.value));
        const currencySymbol = computed(() => getCurrencySymbol(selectCurrency.value));
        const domains = computed(() => props.websites?.map(({ domain }) => domain) || null);
        const yieldLoading = computed(() => store.getters[yieldGetterTypes.isLoading]);
        const loading = computed(() => yieldLoading.value);

        const revenueSelectedPeriod = ref(0);
        const impressionsSelectedPeriod = ref(0);
        const rpmSelectedPeriod = ref(0);

        const yieldData = computed(() => store.getters[yieldGetterTypes.getYieldData]);
        const yieldMetadata = computed(() => store.getters[yieldGetterTypes.getYieldMetadata]);
        const hasValidYieldData = computed(() => yieldData.value?.some((item) => item.impressions > 0) || false);

        const slotNames = computed(() =>
            yieldMetadata.value && yieldMetadata.value.available_slot_names
                ? yieldMetadata.value.available_slot_names
                : []
        );
        const selectedSlot = ref(null);
        watch(
            slotNames,
            (newSlotNames) => {
                if (newSlotNames.length > 0) {
                    selectedSlot.value = newSlotNames[0];
                }
                // Always notify parent — empty list means "no slots for this
                // publisher/web set", and the parent needs to drop stale
                // selections (otherwise selected stays a slot not in items).
                emit("update-available-slot-names", newSlotNames);
            },
            { immediate: true }
        );

        const yieldCsvData = computed(() => store.getters[yieldGetterTypes.getYieldCsvData]);
        const revenueGrandTotal = computed(
            () => (store.getters[yieldGetterTypes.getGrandTotal].total_revenue || 0) / exchangeRate.value
        );
        const impressionsGrandTotal = computed(
            () => store.getters[yieldGetterTypes.getGrandTotal].total_impressions || 0
        );
        const rpmGrandTotal = computed(
            () => (store.getters[yieldGetterTypes.getGrandTotal].total_rpm || 0) / exchangeRate.value
        );

        // Add rpmYesterday computed property
        const rpmYesterday = computed(() => {
            return yieldMetadata.value &&
                yieldMetadata.value["last_valid_row"] &&
                yieldMetadata.value["last_valid_row"]["rpm"]
                ? yieldMetadata.value["last_valid_row"]["rpm"] / exchangeRate.value
                : 0;
        });

        const fetchData = (payload) => {
            // Skip if no website IDs
            if (!payload.aabSiteIds || payload.aabSiteIds.length === 0) {
                return;
            }

            payload.device = device;
            store.dispatch(yieldActionTypes.getGrandTotal, payload);

            store.dispatch(yieldActionTypes.getData, payload).then((data) => {
                // The store resolves superseded (cancelled or late) requests with
                // undefined — keep the current values instead of flashing zeros.
                if (!data) {
                    return;
                }
                revenueSelectedPeriod.value = (data?.metadata?.["total_revenue"] || 0) / exchangeRate.value;
                rpmSelectedPeriod.value = (data?.metadata?.["total_rpm"] || 0) / exchangeRate.value;
                impressionsSelectedPeriod.value = data?.metadata?.["total_impressions"] || 0;
            });
        };

        // Track previous aabSiteIds so we can detect a publisher/website switch and
        // clear metadata BEFORE the new fetch resolves. Without this, the old
        // metadata.available_slot_names triggers an emit during the loading window
        // and the parent's slot dropdown gets repopulated with stale slot names.
        const aabIdsKey = (payload) => (payload?.aabSiteIds || []).slice().sort().join(",");
        let prevAabIdsKey = aabIdsKey(props.payload);
        // The parent's payload is a computed object, so it gets a new reference on
        // every dependency change even when nothing the API cares about changed
        // (e.g. selecting all slots, which maps to the same "no filter"). Only
        // refetch when the content actually differs.
        const payloadKey = (payload) => JSON.stringify(payload);
        let prevPayloadKey = null;
        watch(
            () => props.payload,
            (newPayload) => {
                const newKey = aabIdsKey(newPayload);
                if (newKey !== prevAabIdsKey) {
                    store.commit(yieldMutationTypes.metadataReset);
                    prevAabIdsKey = newKey;
                }
                const contentKey = payloadKey(newPayload);
                if (contentKey === prevPayloadKey) {
                    return;
                }
                prevPayloadKey = contentKey;
                fetchData(newPayload);
            },
            { immediate: true }
        );
        return {
            exchangeRate,
            loading,
            domains,
            isSuperAdmin,
            currencySymbol,
            yieldData,
            yieldMetadata,
            yieldCsvData,
            hasValidYieldData,
            yieldLoading,
            revenueSelectedPeriod,
            revenueGrandTotal,
            impressionsSelectedPeriod,
            impressionsGrandTotal,
            rpmSelectedPeriod,
            rpmGrandTotal,
            rpmYesterday,
        };
    },
};
</script>

<style lang="scss" scoped>
.small-label {
    font-size: 0.8rem;
}
</style>
